import type { DateTime } from "luxon";
import {
	DAYS_IN_WEEK,
	deriveShiftWindows,
	exactWindowKey,
	getMergedDay,
	MONTHLY_OTHER_WINDOW_KEY,
	type MonthlyDayBoundary,
	type MonthlyShiftWindow,
	roundedWindowKey,
} from "@/components/staffing/MonthlyView/jobView/monthlyJobUtils";
import type { Department } from "@/contexts/AuthProvider";
import { getMasterScheduleLocations } from "@/downloads/dailyPrintout/util/locations";
import type { BaseNurseShift, Job, WeekData } from "@/utils/models";
import { ShiftType } from "@/utils/models";
import { computeWindowOverlapMinutes, type TimeWindow } from "@/utils/timeGrouping";

/** Key a shift is placed under when no window fits it. The Monthly view's overflow key, so the two agree. */
export const REMOVE_STAFF_OTHER_WINDOW_KEY = MONTHLY_OTHER_WINDOW_KEY;

/**
 * Share of a shift that must fall inside a window for a custom-hours shift to be listed under it: an 8–3
 * shift sits under 7–3 (all of it inside), and so does a 5a–1p shift (6 of its 8 hours). With only 7–3 and
 * 11p–7a on the date, a 1p–9p shift (2 of its 8 hours inside 7–3, none inside 11p–7a) goes to Other.
 */
export const REMOVE_STAFF_MIN_WINDOW_OVERLAP_RATIO = 0.5;

/** One time window of the date, as the Remove Staff filter pills and grid bands show it. */
export type RemoveStaffWindow = {
	/** `"HH:mm-HH:mm"` over the hour-rounded pair — the Monthly view's window key. */
	key: string;
	/** On the staffing date, day-start aware: a 02:00 window on a 06:00 day start is the next calendar morning. */
	start: DateTime;
	/** Always after `start`: an overnight window's end is on the following day. */
	end: DateTime;
	/** Night band (moon) vs Day band (sun), measured from the department's day start. */
	isNight: boolean;
	/** Compact hours, `"7a–3p"`. */
	label: string;
	/** A configured location asks for staff in this window on this date, so an empty one reads "No one scheduled". */
	isStaffed: boolean;
	/** Shifts placed in this window. */
	shiftCount: number;
};

export type RemoveStaffShiftPlacement = {
	/** A `RemoveStaffWindow.key`, or `REMOVE_STAFF_OTHER_WINDOW_KEY`. */
	windowKey: string;
	/** The shift's exact hours differ from its window's (always true under Other, which has no hours). */
	isCustomHours: boolean;
};

export type RemoveStaffWindows = {
	/** In the department's day order. Only windows that have a shift on the date or are staffed on it. */
	windows: RemoveStaffWindow[];
	/** Shifts no window fits. */
	otherCount: number;
	placementByShiftId: ReadonlyMap<string, RemoveStaffShiftPlacement>;
};

/** Only what placement reads — both scheduled and actual shifts qualify. */
export type RemoveStaffShift = Pick<BaseNurseShift, "id" | "startTime" | "endTime">;

type OfferedWindow = Omit<RemoveStaffWindow, "shiftCount"> & { clock: TimeWindow };

const MINUTES_PER_HOUR = 60;

const clockWindowOf = (start: DateTime, end: DateTime): TimeWindow => ({
	startMin: start.hour * MINUTES_PER_HOUR + start.minute,
	endMin: end.hour * MINUTES_PER_HOUR + end.minute,
});

/** A derived window with its end rolled past midnight, keeping only what this dialog shows. */
const toOfferedWindow = (window: MonthlyShiftWindow): OfferedWindow | null => {
	if (window.start === null || window.end === null) return null;
	// `deriveShiftWindows` keeps an overnight end on the start's calendar day (its key is clock-only).
	const end = window.end <= window.start ? window.end.plus({ days: 1 }) : window.end;
	return {
		key: window.key,
		start: window.start,
		end,
		isNight: window.band === ShiftType.Night,
		label: window.label,
		isStaffed: window.hasTarget,
		clock: clockWindowOf(window.start, end),
	};
};

/** The window a shift belongs to: its rounded window, else the one covering most (≥ 50%) of it, else none. */
const placeShift = (shift: RemoveStaffShift, windows: readonly OfferedWindow[]): RemoveStaffShiftPlacement => {
	const exactKey = exactWindowKey(shift.startTime, shift.endTime);
	const roundedKey = roundedWindowKey(shift.startTime, shift.endTime);
	const rounded = windows.find((window) => window.key === roundedKey);
	if (rounded) return { windowKey: rounded.key, isCustomHours: exactKey !== rounded.key };

	const durationMinutes = shift.endTime.diff(shift.startTime, "minutes").minutes;
	if (durationMinutes <= 0) return { windowKey: REMOVE_STAFF_OTHER_WINDOW_KEY, isCustomHours: true };

	const shiftClock = clockWindowOf(shift.startTime, shift.endTime);
	let best: { window: OfferedWindow; overlap: number } | null = null;
	for (const window of windows) {
		const overlap = computeWindowOverlapMinutes(shiftClock, window.clock);
		// Strictly greater: on a tie the earlier window (day order) keeps the shift.
		if (best === null || overlap > best.overlap) best = { window, overlap };
	}
	if (best !== null && best.overlap / durationMinutes >= REMOVE_STAFF_MIN_WINDOW_OVERLAP_RATIO) {
		return { windowKey: best.window.key, isCustomHours: true };
	}
	return { windowKey: REMOVE_STAFF_OTHER_WINDOW_KEY, isCustomHours: true };
};

/**
 * The date's time windows and which one each of its shifts is listed under.
 *
 * Windows come from `deriveShiftWindows` — the Monthly view's taxonomy — counted over the loaded WEEK (so a
 * one-off shift on this date doesn't mint its own window) and unioned with the locations configured to be
 * staffed on THIS date (`getMasterScheduleLocations`), which is what gives an empty staffed window its
 * "No one scheduled" band. Its caps carry over: hour-rounded, at most `MONTHLY_MAX_SHIFT_WINDOWS`, and a
 * window must occur twice in the week unless it is staffed or wraps past the day start.
 *
 * Membership on the date is NOT recomputed: `week` is already bucketed by staffing day upstream (see
 * `MonthlyDayData`), so `shifts` are taken as the date's own. The day start only orders and anchors windows.
 */
export const deriveRemoveStaffWindows = ({
	week,
	dayIndex,
	showActual,
	shifts,
	visibleJobs,
	department,
}: {
	/** The loaded week that holds the date. */
	week: WeekData;
	/** Sunday-indexed day of the date within `week`. */
	dayIndex: number;
	/** Whether the week's windows are counted from actual or scheduled shifts. */
	showActual: boolean;
	/** The date's shifts to place. */
	shifts: readonly RemoveStaffShift[];
	/** Jobs whose shifts define windows — include `buildUnassignedJob(...)` so a job-less shift still counts. */
	visibleJobs: Job[];
	department: Department;
}): RemoveStaffWindows => {
	const date = getMergedDay(week, dayIndex)?.date;
	if (date === undefined) throw new Error(`deriveRemoveStaffWindows: day ${dayIndex} is not in the loaded week`);

	const dayBoundary: MonthlyDayBoundary = { dayStart: department.dayStart, graceMinutes: department.scheduled_shift_delta_minutes };
	const offered = deriveShiftWindows({
		days: Array.from({ length: DAYS_IN_WEEK }, (_, index) => ({ day: getMergedDay(week, index), showActual })),
		visibleJobs,
		locations: getMasterScheduleLocations(department, date),
		referenceDate: date,
		dayBoundary,
	})
		.map(toOfferedWindow)
		.filter((window): window is OfferedWindow => window !== null);

	const placementByShiftId = new Map<string, RemoveStaffShiftPlacement>();
	const countByKey = new Map<string, number>();
	for (const shift of shifts) {
		const placement = placeShift(shift, offered);
		placementByShiftId.set(shift.id, placement);
		// DEFAULT: a key's first shift starts its count at zero.
		countByKey.set(placement.windowKey, (countByKey.get(placement.windowKey) ?? 0) + 1);
	}

	// DEFAULT: a window no shift was placed in has zero shifts — that is the "No one scheduled" case.
	const windows = offered
		.map(({ clock: _clock, ...window }) => ({ ...window, shiftCount: countByKey.get(window.key) ?? 0 }))
		.filter((window) => window.shiftCount > 0 || window.isStaffed);

	// DEFAULT: no shift was placed under Other.
	return { windows, otherCount: countByKey.get(REMOVE_STAFF_OTHER_WINDOW_KEY) ?? 0, placementByShiftId };
};
